import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

/**
 * Die schwebende Glasleiste des Design-Systems (Liquid-Glass-Muster, iOS 26).
 *
 * Bis September 2026 steckte dieser Aufbau fest in `ui/Navigation.js` — Huelle,
 * Kapsel und Tab-Baustein waren dort private Konstanten. Als das Dashboard
 * dieselbe Leiste bekam (statt eines Hamburger-Schubfachs), waere die Alternative
 * eine zweite handgebaute Leiste gewesen: zwei Geometrien, die auseinanderlaufen,
 * waehrend `--bottom-nav-clearance` (index.css) genau eine Hoehe kennt. Deshalb
 * liegt das Material jetzt hier, und beide Leisten sind Aufrufer.
 *
 * Umgesetzte Vorgaben aus den Design-Standards:
 * - Freischwebende, zentrierte Kapsel statt am Rand klebender Leiste.
 * - ~21pt Abstand zu den Bildschirmkanten (links/rechts/unten).
 * - Beschriftung unter dem Symbol, 11pt — die Glasleiste bleibt sonst ein Raten
 *   nach Piktogrammen. Aktives Ziel in Tinte (seit Oktober 2026, CorporateDesign
 *   §7: „Aktive Reiter und Segmente sind Tinte“), Rest gedämpft.
 * - Text sitzt nie direkt auf durchscheinendem Glas: die Kapsel hinter dem
 *   aktiven Tab legt eine deckende Flaeche unter Symbol und Beschriftung.
 * Das Glas selbst (Blur, Lichtkante, Schatten, Kontrast-Fallbacks) steckt in
 * der Klasse `.liquid-nav` in index.css.
 *
 * Die Leiste bleibt beim Scrollen bewusst unveraendert: Ein Kompakt-Modus, der
 * beim Runterscrollen die Beschriftungen einklappt, war im Test unruhig — die
 * Leiste zappelte bei jeder Scroll-Richtungsaenderung. Konstante Groesse ist
 * hier das ruhigere Verhalten. (Ausprobiert und wieder entfernt: Aug 2026)
 */

// Deckende Kapsel hinter dem aktiven Tab — hellere Glasscheibe auf dem Glas
// (iOS-26-Muster: der gewählte Tab ist eine eigene, leicht erhabene Linse),
// keine harte weiße Fläche mehr.
// Radius 2xl ist hier bewusst: konzentrisch zur 28px-Kapsel (28 px aussen minus
// 12 px Innenabstand), kein Karten-Radius.
// Kontur und Schatten kommen seit Oktober 2026 aus `.glass-hairline` (Haarlinie
// plus neutraler Abwurf, hell und dunkel über Tokens) statt aus rohen
// Inset-Werten; Neobrutalism bekommt dort eine harte Kante (index.css).
export const ActiveCapsule = ({ active }) => (
  <span
    className={`absolute inset-0 rounded-2xl bg-surface/80 dark:bg-foreground/[0.09] glass-hairline transition-[opacity,transform] duration-base ease-standard ${
      active ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
    }`}
    aria-hidden="true"
  />
);

ActiveCapsule.propTypes = {
  active: PropTypes.bool
};

const TAB_CLASSES = 'relative flex flex-col items-center justify-center gap-1 min-w-[56px] flex-1 px-1 py-1.5 rounded-2xl transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-transparent';

/**
 * Ein Navigationsziel: Kapsel + Symbol + Beschriftung.
 *
 * `to` rendert einen Router-Link (Dashboard: echte Routen), `onClick` einen
 * Button (Haupt-App: lokaler `currentView`-Zustand). `badge` rendert optional
 * einen Zähler oder Punkt an der oberen rechten Ecke; Badges stehen in Tinte
 * (`TAB_BADGE_CLASSES`). `muted` hält ein Ziel auch im aktiven Zustand
 * gedämpft — für Ziele, die zwar dauerhaft sichtbar sein sollen, aber nie
 * als „hier bist du“ gelesen werden dürfen (der Ausgang "Zur App" im Dashboard).
 *
 * Aktiv ist Tinte (Oktober 2026): Symbol und Beschriftung `text-foreground`,
 * die Beschriftung halbfett, die Kapsel dahinter. Kein Lila, keine Füllung,
 * kein dickerer Strich — die Kapsel sagt schon, wo man ist. Vorher war das
 * aktive Ziel lila, leicht gefüllt (fill 15 %) und mit Strich 2,25 gezeichnet:
 * drei Signale für eine Information, und eines davon verbrauchte Lila-Budget.
 */
export const TabItem = ({ icon: Icon, label, active, onClick, to, ariaLabel, badge, muted = false, children }) => {
  const content = (
    <>
      <ActiveCapsule active={active} />
      {children || (
        <Icon
          className={`relative h-[22px] w-[22px] transition-colors duration-fast ${
            active && !muted ? 'text-foreground' : 'text-muted-foreground'
          }`}
          aria-hidden="true"
        />
      )}
      <span
        className={`relative text-2xs leading-none tracking-tight transition-colors duration-fast ${
          active && !muted ? 'text-foreground font-semibold' : 'text-muted-foreground font-medium'
        }`}
      >
        {label}
      </span>
      {badge}
    </>
  );

  if (to) {
    return (
      <Link to={to} onClick={onClick} role="tab" aria-selected={!!active} aria-label={ariaLabel || label} className={TAB_CLASSES}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} role="tab" aria-selected={!!active} aria-label={ariaLabel || label} className={TAB_CLASSES}>
      {content}
    </button>
  );
};

/**
 * Klassen für Zähler und Punkte an einem Navigationsziel: Tinte statt Lila,
 * mit einem Schnitt in der Flächenfarbe, damit der Badge sich vom Symbol
 * löst. Dasselbe Literal steht in ChatOverview und NotificationBell.
 */
export const TAB_BADGE_CLASSES = 'bg-foreground text-background ring-2 ring-surface';

TabItem.propTypes = {
  icon: PropTypes.elementType,
  label: PropTypes.string.isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func,
  to: PropTypes.string,
  ariaLabel: PropTypes.string,
  badge: PropTypes.node,
  muted: PropTypes.bool,
  children: PropTypes.node
};

/**
 * @param {Array} items      [{ key, icon, label, active, onClick|to, ariaLabel, badge, muted, children, separatorBefore }]
 * @param {string} ariaLabel Beschriftung der Leiste fuer Screenreader
 * @param {object} navProps  zusaetzliche Attribute fuer das <nav> (z. B. `data-bottom-nav`)
 * @param {boolean} rail     Ab Tablet als schmale Spalte am linken Rand statt als
 *                           Leiste unten. Dieselbe Kapsel, dasselbe Glas, nur
 *                           hochkant — es bleibt bei EINER Navigationsleiste
 *                           (TOKENS.md, Layout-Regel 1). Das Aussehen macht
 *                           `.glass-nav-rail` in index.css; hier faellt nur die
 *                           Entscheidung, ob die Klasse gesetzt wird.
 *                           Standard `false`: Die Dashboard-Leiste bleibt damit
 *                           unveraendert — sie hat ab md: ihre eigene Seitenspalte
 *                           und blendet sich selbst aus.
 */
const GlassTabBar = ({ items, ariaLabel = 'Navigation', className = '', navProps = {}, rail = false }) => (
  <nav
    // Abstaende stehen in `.glass-nav-shell` (index.css) statt hier als
    // Inline-Style: Inline kennt keine Medienabfrage, und als Spalte braucht die
    // Leiste andere Abstaende als unten.
    className={`fixed bottom-0 left-0 right-0 z-50 pointer-events-none keyboard-hide-on-open glass-nav-shell ${rail ? 'glass-nav-rail' : ''} ${className}`}
    {...navProps}
  >
    {/* Freischwebende Glas-Kapsel */}
    <div
      // Fuellung bewusst duenn (50 %/45 %): Liquid Glass ist klares Glas, kein
      // Milchglas — die 65 % von vorher wirkten wie eine weisse Leiste. Blur und
      // Saettigung kommen aus `.liquid-nav` (index.css), nicht aus Tailwind-Klassen,
      // damit es nur EINE Stelle gibt, die das Material definiert.
      className="liquid-nav pointer-events-auto flex justify-around items-stretch gap-0.5 max-w-sm mx-auto rounded-[28px] bg-surface/50 dark:bg-surface/45 px-1.5 py-1.5"
      role="tablist"
      aria-label={ariaLabel}
    >
      {items.map(({ key, separatorBefore, ...item }) => (
        <React.Fragment key={key}>
          {separatorBefore && (
            // Haarlinie statt Farbe: Der Ausgang soll sich absetzen, ohne eine
            // weitere lila Flaeche zu belegen (CD v2.0: hoechstens drei pro Schirm).
            <span className="self-center w-px h-7 shrink-0 bg-foreground/10 mx-0.5" aria-hidden="true" />
          )}
          <TabItem {...item} />
        </React.Fragment>
      ))}
    </div>
  </nav>
);

GlassTabBar.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  navProps: PropTypes.object,
  rail: PropTypes.bool
};

export default GlassTabBar;
